import React, { useEffect, useState } from 'react';
import { usePushNotifications } from '../hooks/usePushNotifications';
import useAuth from '../hooks/useAuth';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CloseIcon from '@mui/icons-material/Close';
import toast from 'react-hot-toast';

const STORAGE_KEY = 'push_notification_asked';

export const AutoPushNotification: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const {
    isSupported,
    isSubscribed,
    isLoading,
    subscribe,
    checkSubscription,
  } = usePushNotifications();

  const [showDialog, setShowDialog] = useState(false);
  const [hasAsked, setHasAsked] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    console.log('[AUTO_PUSH] useEffect check iniciado', { isAuthenticated });
    
    // Só verificar se usuário estiver autenticado
    if (!isAuthenticated) {
      console.log('[AUTO_PUSH] Usuário não autenticado, abortando check');
      return;
    }

    // Verificar subscription atual e permissão
    const check = async () => {
      console.log('[AUTO_PUSH] Iniciando verificação de subscription e permissão');
      const notificationSupported = typeof Notification !== 'undefined';
      const currentPermission = notificationSupported ? Notification.permission : 'default';
      const asked = localStorage.getItem(STORAGE_KEY);
      
      console.log('[AUTO_PUSH] Estado atual', {
        permission: currentPermission,
        hasAskedFlag: !!asked,
        isSupported,
        isSubscribed
      });
      
      // Se já perguntamos antes mas a permissão foi resetada para "default", 
      // limpar o flag para permitir nova solicitação
      if (asked && currentPermission === 'default') {
        localStorage.removeItem(STORAGE_KEY);
        console.log('[AUTO_PUSH] Permissão resetada detectada, limpando flag para permitir nova solicitação');
      }
      
      try {
        await checkSubscription();
        console.log('[AUTO_PUSH] Verificação completa com sucesso');
      } catch (error: any) {
        console.error('[AUTO_PUSH] Erro na verificação, mas continuando', {
          error: error.message
        });
      } finally {
        // Sempre definir checked como true, mesmo se houver erro
        setChecked(true);
        console.log('[AUTO_PUSH] checked=true definido');
      }
    };
    check();
  }, [isAuthenticated, checkSubscription, isSupported, isSubscribed]);

  useEffect(() => {
    console.log('[AUTO_PUSH] useEffect dialog iniciado', { isAuthenticated, checked });
    
    // Só mostrar dialog se usuário estiver autenticado e já verificamos a subscription
    if (!isAuthenticated || !checked) {
      console.log('[AUTO_PUSH] Condições não atendidas para mostrar dialog', { isAuthenticated, checked });
      return;
    }

    // Verificar se já perguntamos antes
    const asked = localStorage.getItem(STORAGE_KEY);
    const notificationSupported = typeof Notification !== 'undefined';
    const currentPermission = notificationSupported ? Notification.permission : 'default';
    
    setHasAsked(!!asked);

    // Mostrar dialog se:
    // 1. Não perguntamos ainda (ou flag foi limpo após reset)
    // 2. O navegador suporta
    // 3. Não está inscrito
    // 4. Permissão está como "default" (não foi negada permanentemente)
    const shouldShow = !asked && isSupported && !isSubscribed && currentPermission === 'default';
    
    console.log('[AUTO_PUSH] Avaliando se deve mostrar dialog', {
      asked: !!asked,
      isSupported,
      isSubscribed,
      permission: currentPermission,
      shouldShow
    });
    
    if (shouldShow) {
      console.log('[AUTO_PUSH] Agendando exibição do dialog em 3 segundos...');
      const timer = setTimeout(() => {
        console.log('[AUTO_PUSH] ✅ Exibindo dialog de notificação');
        setShowDialog(true);
      }, 3000); // Aguarda 3 segundos após carregar a página

      return () => {
        console.log('[AUTO_PUSH] Limpando timer do dialog');
        clearTimeout(timer);
      };
    } else {
      console.log('[AUTO_PUSH] Dialog não será exibido', {
        reason: !asked ? 'já perguntamos' : !isSupported ? 'não suportado' : isSubscribed ? 'já inscrito' : currentPermission !== 'default' ? 'permissão não default' : 'desconhecido'
      });
    }
  }, [isAuthenticated, isSupported, isSubscribed, checked]);

  const handleAccept = async () => {
    console.log('[AUTO_PUSH] Usuário aceitou ativar notificações');
    
    // Não fechar o modal ainda - vamos solicitar permissão primeiro
    // Isso garante que o popup do navegador apareça enquanto o modal ainda está visível
    
    const success = await subscribe();
    
    if (success) {
      console.log('[AUTO_PUSH] ✅ Subscribe bem-sucedido, fechando dialog e salvando flag');
      // Só fechar e salvar após sucesso
      setShowDialog(false);
      localStorage.setItem(STORAGE_KEY, 'true');
      setHasAsked(true);
      toast.success('Notificações ativadas com sucesso!');
    } else {
      console.log('[AUTO_PUSH] ❌ Subscribe falhou, mantendo dialog aberto');
      // Se falhar, manter o modal aberto para o usuário tentar novamente
      // Não salvar o flag ainda, para permitir nova tentativa
      toast.error('Não foi possível ativar as notificações. Tente novamente.');
    }
  };

  const handleDecline = () => {
    console.log('[AUTO_PUSH] Usuário recusou ativar notificações');
    setShowDialog(false);
    localStorage.setItem(STORAGE_KEY, 'true');
    setHasAsked(true);
  };

  // Não mostrar se usuário não estiver autenticado, não suporta, já está inscrito, ou já perguntamos
  if (!isAuthenticated || !isSupported || isSubscribed || hasAsked || !showDialog || !checked) {
    return null;
  }

  return (
    <Dialog
      open={showDialog}
      onClose={handleDecline}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <NotificationsIcon color="primary" sx={{ fontSize: 28 }} />
            <Typography variant="h6" component="span">
              Ativar Notificações?
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={handleDecline}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" color="text.secondary" paragraph>
          Receba notificações sobre seus agendamentos:
        </Typography>
        <Box component="ul" sx={{ pl: 2, mb: 0 }}>
          <Typography component="li" variant="body2" color="text.secondary" paragraph>
            Lembretes 30 minutos antes dos seus agendamentos
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            Avisos quando um agendamento for cancelado
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          onClick={handleDecline}
          variant="outlined"
          color="inherit"
          disabled={isLoading}
        >
          Agora não
        </Button>
        <Button
          onClick={handleAccept}
          variant="contained"
          color="primary"
          disabled={isLoading}
          startIcon={<NotificationsIcon />}
        >
          {isLoading ? 'Ativando...' : 'Ativar Notificações'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

